import { useRef, type CSSProperties } from "react";
import { useChaines } from "./useChaines.js";
import { usePiegeFocus } from "./usePiegeFocus.js";
import { COULEURS, SAUGE, contour, plein, surtitre, titreItalien, verre } from "./guideStyles.js";
import { Losange } from "./icones.js";

/**
 * ============================================================================
 *  ChoixReprise.tsx — « Reprendre là où vous vous étiez arrêté, ou recommencer ? »
 * ----------------------------------------------------------------------------
 *  Posé au lancement d'un parcours dont le visiteur a déjà vu une partie (voir
 *  `repriseProposable`) : deux choix, aucune décision à sa place. Sans lui, la
 *  visite le replaçait d'office au milieu d'un chapitre, ce qui surprend quand on
 *  revient pour la revoir depuis le début.
 *
 *  Il est monté AVANT que le guide ne démarre, sur le noir de l'accueil
 *  (`FonduEntree`) : rien ne joue tant que le visiteur n'a pas répondu.
 *
 *  Dialogue modal : le focus entre sur « Reprendre » (le choix le plus probable), est
 *  piégé dans la boîte, et il n'y a pas d'Échap : fermer sans répondre n'a pas de sens.
 * ==========================================================================*/

export function ChoixReprise({
  chapitre,
  total,
  surReprendre,
  surRecommencer,
}: {
  /** Rang (à partir de 1) du chapitre où l'on reprendrait. */
  chapitre: number;
  total: number;
  surReprendre: () => void;
  surRecommencer: () => void;
}) {
  const { t } = useChaines();
  const racine = useRef<HTMLDivElement>(null);
  const reprendre = useRef<HTMLButtonElement>(null);

  usePiegeFocus(
    racine,
    true,
    () => reprendre.current,
    () => null,
  );

  return (
    <div style={fond}>
      <div ref={racine} role="dialog" aria-modal="true" aria-labelledby="gd-reprise-titre" style={boite}>
        <span style={{ ...surtitre, justifyContent: "center" }}>
          <Losange couleur={SAUGE} />
          {t("chapter")} {t("chapterOf", { n: chapitre, total })}
        </span>
        <h2 id="gd-reprise-titre" style={{ ...titreItalien(28), margin: 0, textAlign: "center" }}>
          {t("resumeTitle")}
        </h2>
        <p style={{ margin: 0, textAlign: "center", fontSize: 14, lineHeight: 1.5, color: "#E3D9C8" }}>
          {t("resumeText", { n: chapitre, total })}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignSelf: "stretch" }}>
          <button ref={reprendre} type="button" onClick={surReprendre} style={{ ...plein, width: "100%" }}>
            {t("resumeContinue")}
          </button>
          <button type="button" onClick={surRecommencer} style={{ ...contour, width: "100%" }}>
            {t("resumeRestart")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Au-dessus du noir de l'accueil (`FonduEntree`, 50) et de l'interface du guide. */
const fond: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 60,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  boxSizing: "border-box",
  pointerEvents: "auto",
};

const boite: CSSProperties = {
  ...verre,
  width: 420,
  maxWidth: "100%",
  boxSizing: "border-box",
  padding: "28px 28px 24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  background: "rgba(16,26,23,.9)",
  color: COULEURS.creme,
};
